import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, Loader2, Home, Sun, Moon } from 'lucide-react';
import axios from 'axios';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

export default function PaymentResultPage({ type }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('');
  const [transaction, setTransaction] = useState(null);
  const [returnUrl, setReturnUrl] = useState(null);

  const orderId = searchParams.get('order_id');
  const gateway = searchParams.get('gateway');
  const externalReturnUrl = searchParams.get('return_url');
  const vivaRef = searchParams.get('ref');
  const vivaOrderCode = searchParams.get('s');
  const vivaTransactionId = searchParams.get('t');
  // PayPal parameters - PayPal adds 'token' (PayPal order ID) and 'PayerID' on redirect
  const paypalToken = searchParams.get('token');
  const paypalPayerId = searchParams.get('PayerID');

  useEffect(() => {
    const verifyPayment = async () => {
      console.log('PaymentResultPage: Starting verification', { type, gateway, orderId, vivaOrderCode, vivaRef, paypalToken, paypalPayerId });
      
      // Get external return URL: from query params OR sessionStorage (saved by PublicPaymentPage)
      const savedReturnUrl = externalReturnUrl || sessionStorage.getItem('alphabit_return_url');
      if (savedReturnUrl) {
        setReturnUrl(savedReturnUrl);
      }
      
      // Check localStorage for Viva Wallet pending order
      let pendingVivaOrder = null;
      try {
        const stored = localStorage.getItem('viva_pending_order');
        console.log('PaymentResultPage: localStorage viva_pending_order =', stored);
        if (stored) {
          pendingVivaOrder = JSON.parse(stored);
          // Only use if less than 30 minutes old
          if (Date.now() - pendingVivaOrder.timestamp > 30 * 60 * 1000) {
            console.log('PaymentResultPage: localStorage expired, clearing');
            pendingVivaOrder = null;
            localStorage.removeItem('viva_pending_order');
          }
        }
      } catch (e) {
        console.error('Error reading localStorage:', e);
      }

      // Determine if this might be a Viva payment
      const isVivaPayment = gateway === 'viva' || vivaOrderCode || pendingVivaOrder;
      console.log('PaymentResultPage: isVivaPayment =', isVivaPayment);
      
      // HANDLE VIVA WALLET CANCEL - Call mark-canceled IMMEDIATELY
      // This bypasses the timing issue where Viva API hasn't updated stateId yet
      if (isVivaPayment && (type === 'cancel' || type === 'failed')) {
        console.log('PaymentResultPage: Viva payment canceled, calling mark-canceled');
        try {
          // Use vivaRef from URL or fallback ref
          const ref = vivaRef || 'fallback';
          console.log('PaymentResultPage: Calling mark-canceled with ref:', ref);
          
          const cancelResponse = await axios.post(`${API_URL}/api/payments/viva/mark-canceled`, { ref });
          console.log('PaymentResultPage: mark-canceled response', cancelResponse.data);
          
          localStorage.removeItem('viva_pending_order');
          
          if (cancelResponse.data.success) {
            setStatus('failed');
            setMessage('Pagamento annullato');
            return;
          }
        } catch (error) {
          console.error('PaymentResultPage: Error marking payment as canceled', error);
        }
        
        // Even if mark-canceled fails, show cancel status
        setStatus('failed');
        setMessage('Pagamento annullato');
        localStorage.removeItem('viva_pending_order');
        return;
      }
      
      // HANDLE VIVA WALLET SUCCESS - Call check-pending
      if (isVivaPayment && type === 'success') {
        try {
          console.log('PaymentResultPage: Calling check-pending to verify Viva payments');
          const checkResponse = await axios.post(`${API_URL}/api/payments/viva/check-pending`);
          console.log('PaymentResultPage: Check-pending response', checkResponse.data);
          
          // Clear localStorage after check
          localStorage.removeItem('viva_pending_order');
          
          // Handle confirmed payments (success page)
          if (checkResponse.data.confirmed > 0) {
            setStatus('success');
            setMessage('Pagamento completato con successo!');
            if (checkResponse.data.transactions?.length > 0) {
              setTransaction(checkResponse.data.transactions[0]);
            }
            return;
          }
          
        } catch (error) {
          console.log('PaymentResultPage: Error checking pending payments', error);
        }
      }
      
      // Try to confirm with specific order code if available (success case)
      if (isVivaPayment && type === 'success') {
        try {
          const orderCode = vivaOrderCode || vivaRef || pendingVivaOrder?.order_code;
          const transactionId = vivaTransactionId || pendingVivaOrder?.transaction_id;
          
          if (orderCode) {
            console.log('PaymentResultPage: Trying to confirm specific order', orderCode);
            const response = await axios.post(`${API_URL}/api/payments/viva/confirm`, {
              order_code: orderCode,
              transaction_id: transactionId
            });
            
            localStorage.removeItem('viva_pending_order');
            
            if (response.data.success) {
              setStatus('success');
              setMessage('Pagamento completato con successo!');
              setTransaction(response.data.transaction);
              return;
            }
          }
        } catch (error) {
          console.error('Viva confirm error:', error);
        }
      }
      
      // Handle PayPal payments - PayPal adds 'token' (PayPal order ID) and 'PayerID' on redirect
      if (paypalToken && paypalPayerId && type === 'success') {
        try {
          console.log('PaymentResultPage: Capturing PayPal payment', { paypalToken, paypalPayerId, orderId });
          // Use paypalToken (the PayPal order ID) for capture
          const response = await axios.post(`${API_URL}/api/payments/capture-order/${paypalToken}`);
          
          if (response.data.status === 'success') {
            // Check if there's an external redirect URL
            if (response.data.redirect_url) {
              console.log('PaymentResultPage: Redirecting to external URL', response.data.redirect_url);
              // Add transaction info to redirect URL
              const redirectUrl = new URL(response.data.redirect_url);
              redirectUrl.searchParams.set('transaction_id', response.data.transaction_id);
              redirectUrl.searchParams.set('amount', response.data.amount);
              redirectUrl.searchParams.set('currency', response.data.currency);
              redirectUrl.searchParams.set('status', 'success');
              window.location.href = redirectUrl.toString();
              return;
            }
            
            setStatus('success');
            setMessage('Pagamento PayPal completato con successo!');
            setTransaction({
              amount: response.data.amount,
              currency: response.data.currency,
              transaction_id: response.data.transaction_id
            });
            return;
          } else {
            console.log('PaymentResultPage: PayPal capture returned non-success', response.data);
            setStatus('failed');
            setMessage(response.data.message || 'Errore nella cattura del pagamento PayPal');
            return;
          }
        } catch (error) {
          console.error('PayPal capture error:', error);
          // Check specific error cases
          if (error.response?.status === 404) {
            // Order not found - might have been already processed
            console.log('PaymentResultPage: PayPal order not found, checking if already completed');
            // Show success if the capture was likely already done
            setStatus('success');
            setMessage('Pagamento già elaborato');
            return;
          } else if (error.response?.status === 400 || error.response?.status === 503) {
            // PayPal API error - show failure
            setStatus('failed');
            setMessage(error.response?.data?.detail || 'Errore nella verifica del pagamento PayPal');
            return;
          }
          // For other errors, show failure
          setStatus('failed');
          setMessage('Errore nella conferma del pagamento PayPal');
          return;
        }
      }

      // Handle PayPal cancel
      if (paypalToken && type === 'cancel') {
        console.log('PaymentResultPage: PayPal payment cancelled', { paypalToken });
        setStatus('failed');
        setMessage('Pagamento PayPal annullato');
        return;
      }

      // Handle NEXI payments - backend already processed everything in the callback
      // Just show the result based on URL type, no API call needed
      if (gateway === 'nexi') {
        console.log('PaymentResultPage: NEXI payment detected, showing result based on type', { type, orderId });
        if (type === 'success') {
          setStatus('success');
          setMessage('Pagamento NEXI completato con successo!');
        } else if (type === 'cancel') {
          setStatus('failed');
          setMessage('Pagamento NEXI annullato');
        } else {
          setStatus('failed');
          setMessage('Pagamento NEXI non riuscito');
        }
        return;
      }

      // Handle RoxPay payments
      if (orderId && !paypalToken) {
        try {
          console.log('PaymentResultPage: Confirming RoxPay payment', { orderId, type });
          const response = await axios.post(`${API_URL}/api/payments/roxpay/confirm`, {
            order_id: orderId,
            status: type
          });

          if (response.data.success) {
            // Check if there's an external redirect URL (multi-skin auto-redirect)
            if (response.data.redirect_url) {
              console.log('PaymentResultPage: Redirecting to external URL', response.data.redirect_url);
              // Add transaction info to redirect URL
              const redirectUrl = new URL(response.data.redirect_url);
              if (response.data.transaction) {
                redirectUrl.searchParams.set('transaction_id', response.data.transaction.id);
                redirectUrl.searchParams.set('amount', response.data.transaction.amount);
                redirectUrl.searchParams.set('currency', response.data.transaction.currency);
              }
              redirectUrl.searchParams.set('status', 'success');
              window.location.href = redirectUrl.toString();
              return;
            }
            
            setStatus('success');
            setMessage('Pagamento completato con successo!');
            setTransaction(response.data.transaction);
            // Use return_url from confirm response for "Torna al Negozio" button
            if (response.data.return_url && !savedReturnUrl) {
              setReturnUrl(response.data.return_url);
            }
          } else if (response.data.status === 'processing') {
            // Payment is being verified - show processing state
            setStatus('loading');
            setMessage(response.data.message || 'Pagamento in elaborazione...');
            // Optionally poll for status or wait for redirect
          } else {
            // Payment verification failed or payment was cancelled
            setStatus('failed');
            setMessage(response.data.message || 'Pagamento non completato');
          }
          return;
        } catch (error) {
          console.error('RoxPay verify error:', error);
        }
      }

      // Default: show based on type
      localStorage.removeItem('viva_pending_order');
      setStatus(type === 'success' ? 'success' : 'failed');
      setMessage(type === 'success' ? 'Pagamento completato!' : 'Pagamento annullato');
    };

    verifyPayment();
  }, [orderId, type, gateway, vivaRef, vivaOrderCode, vivaTransactionId]);

  const isSuccess = status === 'success';
  const isLoading = status === 'loading';

  return (
    <div className={`min-h-screen flex items-center justify-center p-4 ${
      isDark ? 'bg-[#0B0F2A]' : 'bg-gradient-to-b from-slate-50 to-slate-100'
    }`}>
      {/* Theme Toggle */}
      <button 
        onClick={toggleTheme}
        className={`fixed top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full transition-all hover:scale-110 ${
          isDark 
            ? 'bg-purple-500/20 text-yellow-400 hover:bg-purple-500/30' 
            : 'bg-white text-slate-600 hover:bg-slate-100 shadow-md'
        }`}
        data-testid="theme-toggle"
      >
        {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
      </button>

      <Card className={`w-full max-w-md shadow-xl ${
        isDark 
          ? 'bg-[#0F0F2F] border-purple-500/30' 
          : 'bg-white border-slate-200'
      }`}>
        <CardContent className="p-8 text-center">
          {isLoading ? (
            <>
              <Loader2 className={`w-16 h-16 mx-auto animate-spin mb-4 ${
                isDark ? 'text-[#9D4DFF]' : 'text-primary'
              }`} />
              <h1 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-slate-800'}`}>
                Verifica pagamento in corso...
              </h1>
            </>
          ) : isSuccess ? (
            <>
              <div className={`w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-6 ${
                isDark ? 'bg-green-500/20' : 'bg-green-100'
              }`}>
                <CheckCircle className={`w-12 h-12 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
              </div>
              <h1 className={`text-2xl font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Pagamento Completato!
              </h1>
              <p className={`mb-6 ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>{message}</p>
              {transaction && (
                <div className={`rounded-lg p-4 mb-6 text-left ${
                  isDark ? 'bg-purple-500/10 border border-purple-500/20' : 'bg-slate-50'
                }`}>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>Importo pagato</p>
                  <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    €{transaction.amount?.toFixed(2)}
                  </p>
                  <p className={`text-xs mt-2 ${isDark ? 'text-gray-500' : 'text-slate-400'}`}>
                    ID: {transaction.id?.substring(0, 8)}...
                  </p>
                </div>
              )}
            </>
          ) : (
            <>
              <div className={`w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-6 ${
                isDark ? 'bg-red-500/20' : 'bg-red-100'
              }`}>
                <XCircle className={`w-12 h-12 ${isDark ? 'text-red-400' : 'text-red-600'}`} />
              </div>
              <h1 className={`text-2xl font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                {type === 'cancel' ? 'Pagamento Annullato' : 'Pagamento Non Riuscito'}
              </h1>
              <p className={`mb-6 ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>{message}</p>
            </>
          )}

          <Button
            onClick={() => {
              if (returnUrl) {
                window.location.href = returnUrl;
              } else {
                navigate('/');
              }
            }}
            className={`w-full ${
              isSuccess 
                ? isDark 
                  ? 'bg-gradient-to-r from-[#6A00FF] to-[#9D4DFF] hover:from-[#7B1FFF] hover:to-[#AE6EFF]' 
                  : 'bg-primary hover:bg-primary/90'
                : isDark
                  ? 'bg-transparent border border-purple-500/30 text-white hover:bg-purple-500/10'
                  : ''
            }`}
            variant={isSuccess ? 'default' : 'outline'}
            data-testid="return-home-button"
          >
            <Home className="w-4 h-4 mr-2" />
            {returnUrl ? 'Torna al Negozio' : 'Torna alla Home'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
